import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import useCreateTemplateAction, {
  type CreateTemplateAction,
} from "work/proposals/_views/_shared/use-create-template-action";
import { useCreateTeamTemplateMutation } from "work/team-templates/_api/team-template-api";
import TeamTemplate from "work/team-templates/team-template";
import { TeamTemplateParty } from "work/team-templates/team-template-party";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import VendorTeamName from "work/work-agreements/teams/vendor-team/vendor-team-name/vendor-team-name";

export type CreateClientTeamTemplateActionOptions = {
  /** The Client Representative. A client team template cannot be saved without one. */
  leader: EntityMember | null | undefined;
  /** The client team's members as the page is holding them, which is who the template saves. */
  members: readonly EntityMember[];
  /** The proposal's name, which the template's default name is made from. */
  proposalName?: string | null;
};

/**
 * Save as Template for a proposal's Client Team tab: saves the client team as a client-party team
 * template (#5160). It sits in the roster's secondary actions, beside Add Member, as the other two
 * tabs' do (#5394).
 *
 * It sends the roster the page is holding, so a draft that has never been saved can be saved as a
 * template too.
 */
export default function useCreateClientTeamTemplateAction(
  options: Readonly<CreateClientTeamTemplateActionOptions>,
): CreateTemplateAction {
  const { leader, members, proposalName } = options;
  const session = useSession();
  const [createTeamTemplate] = useCreateTeamTemplateMutation();
  const entityId = session.user?.legalEntityId;

  async function handleCreate(name: string): Promise<boolean> {
    if (!leader || !entityId) return false;
    const created = await saveClientTeamTemplate(createTeamTemplate, {
      entityId,
      name,
      leader,
      members: [...members],
      creatorType: session.accountType,
    });
    return Boolean(created);
  }

  return useCreateTemplateAction({
    dialogTitle: "Save as Client Team Template",
    disabledReason: leader && entityId ? undefined : "Name a Client Representative before saving a template",
    defaultName: getDefaultClientTeamTemplateName(proposalName),
    onCreate: handleCreate,
  });
}

export function getDefaultClientTeamTemplateName(proposalName?: string | null): string {
  const trimmedName = proposalName?.trim();
  return trimmedName ? `${trimmedName} Client Team` : "Proposal Client Team";
}

/**
 * Sends one create and reports the template it made, or undefined once the user has been told why
 * there is none.
 *
 * At module scope because it holds a `try`/`catch` around an `await` — a component that contains one
 * is emitted uncompiled in its entirety, memoization and all (see app/CLAUDE.md).
 */
async function saveClientTeamTemplate(
  createTeamTemplate: ReturnType<typeof useCreateTeamTemplateMutation>[0],
  request: {
    entityId: Guid;
    name: string;
    leader: EntityMember;
    members: EntityMember[];
    creatorType: AccountType;
  },
): Promise<TeamTemplate | undefined> {
  try {
    const response = await createTeamTemplate({
      entityId: request.entityId.value,
      // `VendorTeam` is the create request's leader-plus-members carrier rather than a statement that
      // this is a vendor team — `party` below is what says which side it is, and the server records
      // it. `TeamTemplate.AsVendorTeam` is where a client template is stopped from being read back as
      // one.
      team: new VendorTeam({ leader: request.leader, members: request.members }),
      teamName: new VendorTeamName(request.name),
      creatorType: request.creatorType,
      party: TeamTemplateParty.Client,
    }).unwrap();
    enqueueSnackbar("Client team template created", { variant: "success" });
    return TeamTemplate.fromJSON(response);
  } catch (error) {
    console.error("Failed to create client team template", error);
    showErrorSnackbar(error, { doing: "creating the client team template", operation: "proposal.create-client-team-template" });
    return undefined;
  }
}
